// routes
import { refreshLogin } from 'src/api';
import { PATH_AUTH } from '../routes/paths';
// utils
import axios from '../utils/axios';
// types
import { RefreshTokenResponseType } from './types';

// 全局导航函数，会在适当时候设置
let globalNavigate: ((path: string) => void) | null = null;

// 设置全局导航函数（在应用初始化时调用）
export const setGlobalNavigate = (navigate: (path: string) => void) => {
  globalNavigate = navigate;
};

// 安全的页面跳转函数
const safeNavigate = (path: string) => {
  if (globalNavigate) {
    console.log('使用React Router导航到:', path);
    globalNavigate(path);
  } else {
    console.log('使用window.location跳转到:', path);
    window.location.href = path;
  }
};

// ----------------------------------------------------------------------

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  console.log('JWT payload:', jsonPayload);
  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    console.log('Token不存在');
    return false;
  }

  try {
    const decoded = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;
    
    console.log('Token过期时间:', decoded.exp, '当前时间:', currentTime);
    
    const isValid = decoded.exp > currentTime;
    console.log('Token是否有效:', isValid);
    
    return isValid;
  } catch (error) {
    console.error('Token解析失败:', error);
    return false;
  }
};

// ----------------------------------------------------------------------

export const getUserRoleFromToken = (accessToken: string): string | null => {
  try {
    if (!accessToken) return null;
    const decoded = jwtDecode(accessToken);
    return decoded.role || null;
  } catch (error) {
    console.error('提取用户角色失败:', error);
    return null;
  }
};

// ----------------------------------------------------------------------

export const tokenExpired = (exp: number) => {
  let expiredTimer: string | number | NodeJS.Timeout | undefined;
  const currentTime = Date.now();
  // 提前30秒刷新token（用于测试）
  const timeLeft = exp * 1000 - currentTime - 30000;

  console.log('设置token过期检查，距离过期时间:', timeLeft, 'ms');

  clearTimeout(expiredTimer);

  // 如果时间已经过期，立即处理
  if (timeLeft <= 0) {
    const logoutInfo = {
      reason: 'Token已过期（立即检查）',
      timestamp: new Date().toISOString(),
      expiredAt: new Date(exp * 1000).toISOString(),
      currentTime: new Date().toISOString()
    };
    console.log('Token已经过期，立即清除', logoutInfo);
    
    // 保存失效信息到localStorage，供调试使用
    localStorage.setItem('lastLogoutInfo', JSON.stringify(logoutInfo));
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    // 直接跳转，保持控制台日志
    console.warn('🔄 跳转到登录页，控制台日志已保留...');
    safeNavigate(PATH_AUTH.login);
    return;
  }

  expiredTimer = setTimeout(async () => {
    console.log('开始执行token过期检查...');
    
    if (!localStorage.getItem('refresh_token') || !localStorage.getItem('accessToken')) {
      console.log('缺少必要的token，清除登录状态');
      clearTimeout(expiredTimer);
      return;
    }
    
    try {
      console.log('尝试刷新token...');
      // 调用刷新token接口
      const response = await refreshLogin({
        refresh_token: localStorage.getItem('refresh_token')
      }) as RefreshTokenResponseType;
      
      console.log('Token refresh response:', response);
      
      // 检查响应状态码
      if (response.code === 200 && response.data.access_token) {
        // 如果成功获取新token，更新session
        setSession(response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        console.log('✅ Token刷新成功');
      } else {
        // 如果刷新失败，清除登录状态并跳转到登录页
        const logoutInfo = {
          reason: 'Token刷新失败',
          timestamp: new Date().toISOString(),
          response: response,
          errorMessage: response.message
        };
        console.error('❌ Token刷新失败:', logoutInfo);
        localStorage.setItem('lastLogoutInfo', JSON.stringify(logoutInfo));
        
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        console.warn('🔄 跳转到登录页，刷新失败详情已保存...');
        safeNavigate(PATH_AUTH.login);
      }
    } catch (error) {
      // 发生错误时，清除登录状态并跳转到登录页
      const logoutInfo = {
        reason: 'Token刷新异常',
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      };
      console.error('❌ Token刷新出错:', logoutInfo);
      localStorage.setItem('lastLogoutInfo', JSON.stringify(logoutInfo));
      
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      console.warn('🔄 跳转到登录页，错误详情已保存...');
      safeNavigate(PATH_AUTH.login);
    }
  }, timeLeft);
};

// ----------------------------------------------------------------------

export const setSession = (accessToken: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    // This function below will handle when token is expired
    const { exp } = jwtDecode(accessToken); // ~3 days by minimals server

    tokenExpired(exp);
  } else {
    // 清除所有认证相关的存储数据
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');

    delete axios.defaults.headers.common.Authorization;
  }
};

// ----------------------------------------------------------------------

// 测试用：手动触发token刷新
export const testRefreshToken = async () => {
  console.log('=== 开始测试刷新token ===');
  
  const refreshToken = localStorage.getItem('refresh_token');
  const accessToken = localStorage.getItem('accessToken');
  
  if (!refreshToken || !accessToken) {
    console.error('测试失败：缺少token');
    return;
  }
  
  try {
    // 调用刷新token接口
    const response = await refreshLogin({
      refresh_token: refreshToken
    }) as RefreshTokenResponseType;
    
    console.log('刷新token响应:', response);
    
    // 检查响应状态码
    if (response.code === 200 && response.data.access_token) {
      // 如果成功获取新token，更新session
      setSession(response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      console.log('✅ Token刷新成功');
      console.log('新的access_token:', response.data.access_token);
    } else {
      console.error('❌ Token刷新失败:', response.message);
    }
  } catch (error) {
    console.error('❌ Token刷新出错:', error);
  }
  
  console.log('=== 结束测试刷新token ===');
};

// 查看上次登出的详细信息
export const getLastLogoutInfo = () => {
  try {
    const info = localStorage.getItem('lastLogoutInfo');
    if (info) {
      const logoutData = JSON.parse(info);
      console.group('📋 上次登出详情');
      console.log('失效原因:', logoutData.reason);
      console.log('失效时间:', logoutData.timestamp);
      if (logoutData.expiredAt) {
        console.log('Token过期时间:', logoutData.expiredAt);
      }
      if (logoutData.errorMessage) {
        console.log('错误信息:', logoutData.errorMessage);
      }
      if (logoutData.url) {
        console.log('请求URL:', logoutData.url);
      }
      console.log('完整数据:', logoutData);
      console.groupEnd();
      return logoutData;
    } else {
      console.log('没有找到上次登出记录');
      return null;
    }
  } catch (error) {
    console.error('解析登出信息失败:', error);
    return null;
  }
};

// 清除登出历史记录
export const clearLogoutHistory = () => {
  localStorage.removeItem('lastLogoutInfo');
  console.log('✅ 已清除登出历史记录');
};

// 将测试函数暴露到全局，方便在控制台调用
if (typeof window !== 'undefined') {
  (window as any).testRefreshToken = testRefreshToken;
  (window as any).getLastLogoutInfo = getLastLogoutInfo;
  (window as any).clearLogoutHistory = clearLogoutHistory;
}
